import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Printer } from 'lucide-react';

const ContratView = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadContrat();
  }, [id]);

  // Add print styles
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @media print {
        @page {
          size: A4;
          margin: 15mm;
        }
        
        body {
          print-color-adjust: exact;
          -webkit-print-color-adjust: exact;
        }
        
        .no-print {
          display: none !important;
        }
        
        /* Reduce padding for print */
        .p-8, .p-6 {
          padding: 15px !important;
        }
        
        .py-8 {
          padding-top: 15px !important;
          padding-bottom: 15px !important;
        }
        
        /* Avoid page breaks in sections */
        section, .border-b {
          page-break-inside: avoid;
        }
        
        h1 { font-size: 20px !important; }
        h2 { font-size: 16px !important; }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const loadContrat = async () => {
    try {
      const res = await axios.get(`${API}/contrats/${id}`);
      setData(res.data);
    } catch (error) {
      console.error('Error loading contrat:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center">
        <p>Contrat introuvable</p>
      </div>
    );
  }

  const { contrat, devis } = data;
  const clientInfo = devis?.client_info || {};
  const eventInfo = devis?.event_info || {};
  
  // Extract dates and locations
  const uniqueDates = [...new Set((eventInfo.dates || []).map(d => d.date))];
  const dateDisplay = uniqueDates.length > 1 
    ? uniqueDates.map(formatDate).join(' & ') 
    : formatDate(uniqueDates[0]);
  
  const lieuxList = (eventInfo.dates || []).map(d => `${d.type}: ${d.lieu}`).join(', ');
  
  // Extract prestations by category
  const prestasVideo = (devis?.lines || []).filter(l => l.prestation_name.toLowerCase().includes('vidéo'));
  const prestasPhoto = (devis?.lines || []).filter(l => l.prestation_name.toLowerCase().includes('photo'));
  const prestasMagnets = (devis?.lines || []).filter(l => l.prestation_name.toLowerCase().includes('magnet'));
  const prestasDrone = (devis?.lines || []).filter(l => l.prestation_name.toLowerCase().includes('drone'));
  
  const nomPrestaVideo = prestasVideo.length > 0 ? prestasVideo.map(p => p.prestation_name).join(', ') : 'Non souscrit';
  const nomPrestaPhoto = prestasPhoto.length > 0 ? prestasPhoto.map(p => p.prestation_name).join(', ') : 'Non souscrit';
  const nomPrestaMagnets = prestasMagnets.length > 0 ? prestasMagnets.map(p => p.prestation_name).join(', ') : 'Non';
  const nomPrestaDrone = prestasDrone.length > 0 ? prestasDrone.map(p => p.prestation_name).join(', ') : 'Non';
  
  const eventTypeLabels = {
    'mariage': 'Mariage',
    'barmitzvah': 'Bar/Bat Mitsvah',
    'brit_mila': 'Brit Mila',
    'coupe_cheveux': 'Coupe de Cheveux',
    'autre': 'Autre événement'
  };

  return (
    <div className="min-h-screen bg-stone-100 py-8 px-4">
      {/* Actions bar */}
      <div className="max-w-4xl mx-auto mb-6 flex items-center justify-between no-print">
        <Link to="/backoffice/contrats" className="flex items-center gap-2 text-stone-600 hover:text-stone-800">
          <ArrowLeft className="h-4 w-4" />
          Retour aux contrats
        </Link>
        <Button variant="outline" onClick={handlePrint}>
          <Printer className="h-4 w-4 mr-2" />
          Imprimer
        </Button>
      </div>

      {/* Document */}
      <div className="max-w-4xl mx-auto bg-white shadow-lg print:shadow-none" style={{ fontSize: '12px', lineHeight: '1.6' }}>
        {/* Header */}
        <div className="p-8 border-b text-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_wedding-erp/artifacts/6y9ywpy7_image.png" 
            alt="Kolnoa" 
            className="h-20 mx-auto mb-4"
          />
          <h1 className="text-xl font-bold text-stone-800 mb-1">CONTRAT DE PRESTATION AUDIOVISUELLE</h1>
        </div>

        {/* Content */}
        <div className="p-8 space-y-6">
          {/* Parties */}
          <div className="space-y-4">
            <h2 className="font-bold text-base">ENTRE LES SOUSSIGNÉS :</h2>
            
            <div>
              <p><strong>D'une part :</strong> La Société <strong>KOLNOA PRODUCTION</strong></p>
              <p>Siège social : 99, Rue de Crimée - 75019 Paris</p>
              <p>Représentée par M. Jeremy FERRARA</p>
              <p>Email : kolnoaprod@gmail.com - Tél : 06.68.11.93.43</p>
            </div>

            <div>
              <p><strong>ET D'autre part :</strong> Le Client :</p>
              <p><strong>{clientInfo.prenom} {clientInfo.nom}</strong></p>
              <p>Adresse : {clientInfo.adresse || 'Non renseignée'}, {clientInfo.code_postal || ''} {clientInfo.ville || ''}</p>
              <p>Email : {clientInfo.email} - Tél : {clientInfo.telephone}</p>
            </div>

            <p className="font-semibold text-center mt-4">IL A ÉTÉ CONVENU CE QUI SUIT :</p>
          </div>

          {/* ARTICLE 1 */}
          <div>
            <h3 className="font-bold mb-2">ARTICLE 1 : OBJET</h3>
            <p>Le Client engage le Prestataire pour la réalisation d'un reportage (Photo/Vidéo) à l'occasion de l'événement suivant :</p>
            <ul className="list-none ml-4 mt-2">
              <li>• <strong>Type :</strong> {eventTypeLabels[eventInfo.event_type] || eventInfo.event_type}</li>
              <li>• <strong>Date(s) :</strong> {dateDisplay || 'À définir'}</li>
              <li>• <strong>Lieu(x) :</strong> {lieuxList || 'À définir'}</li>
            </ul>
          </div>

          {/* ARTICLE 2 */}
          <div>
            <h3 className="font-bold mb-2">ARTICLE 2 : PRESTATIONS ET TARIFS</h3>
            <p>Le détail des prestations choisies figure dans le Devis N° <strong>{devis?.numero}</strong> joint au présent contrat. Le montant total de la prestation est fixé à : <strong>{devis?.total_ttc?.toLocaleString()} € TTC</strong>.</p>
            <ul className="list-none ml-4 mt-2">
              <li>• <strong>Prestation Vidéo :</strong> {nomPrestaVideo}</li>
              <li>• <strong>Prestation Photo :</strong> {nomPrestaPhoto}</li>
              <li>• <strong>Options souscrites :</strong> {[nomPrestaMagnets, nomPrestaDrone].filter(p => p !== 'Non').join(', ') || 'Aucune'}</li>
            </ul>
            <p className="mt-2">Le détail complet (horaires, livrables techniques) et tarifaire figure dans le Devis N° <strong>{devis?.numero}</strong> joint aux présentes. Le montant total de la prestation est arrêté à la somme de : <strong>{devis?.total_ttc?.toLocaleString()} € TTC</strong>.</p>
          </div>

          {/* ARTICLE 3 */}
          <div>
            <h3 className="font-bold mb-2">ARTICLE 3 : MODALITÉS DE PAIEMENT</h3>
            <p>Le paiement s'effectuera selon l'échéancier suivant :</p>
            <ol className="list-decimal ml-8 mt-2 space-y-2">
              <li>
                <p><strong>Acompte de {devis?.acompte_percent}% à la signature du présent contrat</strong>, soit la somme de : <strong>{devis?.acompte_euros?.toLocaleString()} €</strong>.</p>
                <ul className="list-none ml-4 mt-1">
                  <li>○ La réservation de la date ne sera effective qu'à réception de cet acompte.</li>
                  <li>○ <strong>Annulation :</strong> Cet acompte est non remboursable et restera définitivement acquis à la société Kolnoa Production en cas d'annulation de l'événement par le Client.</li>
                  <li>○ <strong>Report de date :</strong> Si la date de l'événement est décalée pour quelque raison que ce soit, l'acompte sera conservé et reporté sur la nouvelle date. Le présent contrat et les tarifs validés au devis resteront fermes et garantis pour cette nouvelle date, sans aucune augmentation, même si la société a augmenté ses tarifs entre-temps.</li>
                </ul>
              </li>
              <li><strong>Le Solde</strong> devra être réglé le jour de l'événement au plus tard.</li>
            </ol>
          </div>

          {/* ARTICLE 4 */}
          <div>
            <h3 className="font-bold mb-2">ARTICLE 4 : LIVRAISON</h3>
            <p>{devis?.livraison_phrase || 'Livraison sur Clé USB Boitier personnalisé du film version longue et Teaser et Intégrale du Reportage Photo en HD'}</p>
            <p>Le délai de livraison est variable après l'événement. Aucun délai ne peut vous être garanti.</p>
          </div>

          {/* ARTICLE 5 */}
          <div>
            <h3 className="font-bold mb-2">ARTICLE 5 : EXCLUSIVITÉ</h3>
            <p>La société Kolnoa et ses employés sont les seuls vidéastes/photographes officiels de votre événement. Dans le cas contraire, si un autre prestataire professionnel est présent à votre événement sans accord préalable, l'équipe de reportage se réserve le droit de quitter immédiatement les lieux et de mettre fin au dit contrat. Dans ce cas, aucun remboursement des sommes versées ne sera effectué.</p>
          </div>

          {/* ARTICLE 6 */}
          <div>
            <h3 className="font-bold mb-2">ARTICLE 6 : DROIT À L'IMAGE ET RÉSEAUX SOCIAUX</h3>
            <p>Sauf avis contraire exprimé par écrit avant le jour de l'événement, le Client autorise la société Kolnoa Production à utiliser les images (photographies et extraits vidéo) réalisées lors de la prestation à des fins promotionnelles et artistiques.</p>
            <p className="mt-2">Cette autorisation comprend notamment la publication sur :</p>
            <ul className="list-none ml-4 mt-1">
              <li>• Le site internet officiel de Kolnoa Production.</li>
              <li>• Les réseaux sociaux professionnels, et spécifiquement le compte Instagram @kolnoa.prod.</li>
            </ul>
            <p className="mt-2">Le Prestataire s'engage à ce que les images utilisées ne portent pas atteinte à la vie privée ou à la réputation du Client et soient utilisées dans un cadre strictement professionnel pour présenter la qualité de son travail.</p>
          </div>

          {/* ARTICLE 7 */}
          <div>
            <h3 className="font-bold mb-2">ARTICLE 7 : MODIFICATION ET ANNULATION</h3>
            <ul className="list-none ml-4 space-y-2">
              <li>• <strong>Modification :</strong> Toute modification du contrat demandée par le client doit être acceptée par écrit par le prestataire. Le prix pourra être révisé en conséquence.</li>
              <li>• <strong>Annulation :</strong> En cas d'annulation par le client, l'acompte versé restera acquis au Prestataire à titre d'indemnité, sauf cas de force majeure avéré.</li>
            </ul>
          </div>

          {/* ARTICLE 8 */}
          <div>
            <h3 className="font-bold mb-2">ARTICLE 8 : LITIGES</h3>
            <p>Le présent contrat est soumis aux lois et règlements français. En cas de difficultés sur l'interprétation ou l'exécution du présent contrat, les parties s'efforceront de résoudre le différend à l'amiable. En cas de désaccord persistant, les tribunaux compétents seront saisis.</p>
          </div>

          {/* ARTICLE 9 */}
          <div>
            <h3 className="font-bold mb-2">ARTICLE 9 : CONDITIONS SPÉCIFIQUES - PRESTATION DRONE</h3>
            <p>La réalisation de prises de vues aériennes par drone est soumise à des contraintes techniques, météorologiques et réglementaires strictes. Le télépilote est seul juge de la faisabilité du vol.</p>
            
            <div className="mt-2">
              <p className="font-semibold">9.1. Conditions de vol et Sécurité</p>
              <ul className="list-none ml-4 mt-1">
                <li>• <strong>Lieux autorisés :</strong> Les vols s'effectuent exclusivement sur terrain privé (jardins du domaine, château, parc). Aucun vol ne sera réalisé sur la voie publique ou en zone urbaine interdite.</li>
                <li>• <strong>Autorisation du lieu :</strong> Le Client doit s'assurer que la salle de réception autorise explicitement le vol de drone dans son enceinte (intérieur et extérieur).</li>
                <li>• <strong>Vol en intérieur :</strong> Il ne sera réalisé que si le propriétaire des lieux l'autorise et que la configuration le permet en toute sécurité (hauteur de plafond suffisante, absence d'obstacles ou de décorations dangereuses).</li>
              </ul>
            </div>

            <div className="mt-2">
              <p className="font-semibold">9.2. Annulation et Facturation</p>
              <p>Si la prestation drone ne peut être réalisée pour cause de météo défavorable (pluie, vent fort), de configuration des lieux inadaptée, de refus du propriétaire de la salle ou de tout autre problème de sécurité :</p>
              <ul className="list-none ml-4 mt-1">
                <li>• <strong>Si l'option a été facturée :</strong> Son montant sera intégralement déduit du solde restant à payer par le Client.</li>
                <li>• <strong>Si l'option a été offerte :</strong> La prestation sera simplement annulée et ne donnera lieu à aucune compensation financière ni indemnité.</li>
              </ul>
            </div>
          </div>

          {/* Signatures */}
          <div className="mt-12 grid grid-cols-2 gap-8">
            <div>
              <p className="font-bold text-center mb-8">LE PRESTATAIRE</p>
              <p className="text-center">KOLNOA PRODUCTION</p>
              <div className="border-t border-stone-300 mt-16 pt-2 text-center text-sm">
                (Signature)
              </div>
            </div>
            <div>
              <p className="font-bold text-center mb-8">LE CLIENT</p>
              <p className="text-center">{clientInfo.prenom} {clientInfo.nom}</p>
              <div className="border-t border-stone-300 mt-16 pt-2 text-center text-sm">
                (Mention "Lu et approuvé" + Signature)
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { margin: 0; padding: 0; }
          @page { margin: 2cm; }
        }
      `}</style>
    </div>
  );
};

export default ContratView;
